// Math problem solver + step generator.
//
// Detects problem type from input string, then dispatches to specialised
// step-generators that produce a sequence of human-readable steps in
// English + Bengali. Falls back to mathjs evaluation/simplification.

import {
  parse,
  simplify,
  derivative,
  evaluate,
  rationalize,
  type MathNode,
} from "mathjs";

export type ProblemType =
  | "auto"
  | "equation"
  | "system"
  | "quadratic"
  | "simplify"
  | "factor"
  | "expand"
  | "differentiate"
  | "arithmetic";

export interface Step {
  en: string;
  bn: string;
  expr?: string; // LaTeX-friendly expression (mathjs syntax)
}

export interface SolverResult {
  ok: boolean;
  type: ProblemType;
  problem: string; // normalised input
  problemTex: string;
  answer?: string; // simple text answer like "x = 4"
  answerTex?: string;
  steps: Step[];
  error?: { en: string; bn: string };
}

// ---------------------------------------------------------------------------
// Input normalisation
// ---------------------------------------------------------------------------
const SUPER_MAP: Record<string, string> = {
  "²": "^2", "³": "^3", "⁴": "^4", "⁵": "^5", "⁶": "^6", "⁷": "^7",
  "⁸": "^8", "⁹": "^9", "⁰": "^0", "¹": "^1",
};

export function normalize(raw: string): string {
  let s = raw.trim();
  s = s.replace(/[²³⁴⁵⁶⁷⁸⁹⁰¹]/g, (c) => SUPER_MAP[c] ?? c);
  s = s.replace(/×/g, "*").replace(/÷/g, "/").replace(/−/g, "-").replace(/–/g, "-");
  s = s.replace(/√/g, "sqrt");
  s = s.replace(/π/g, "pi");
  // Insert * between digit and letter or letter and (
  s = s.replace(/(\d)\s*([a-zA-Z\(])/g, "$1*$2");
  s = s.replace(/(\))\s*([a-zA-Z\d\(])/g, "$1*$2");
  // Collapse whitespace
  s = s.replace(/\s+/g, "");
  return s;
}

// ---------------------------------------------------------------------------
// Auto-detect problem type
// ---------------------------------------------------------------------------
export function detectType(raw: string): ProblemType {
  const s = raw.trim();
  if (/;|\n/.test(s) && /=/.test(s)) return "system";
  if (/=/.test(s)) {
    if (/x\^?2|x\*x/.test(s.replace(/\s/g, "")) ) return "quadratic";
    return "equation";
  }
  if (/^d\/?dx/i.test(s) || /^differentiate/i.test(s)) return "differentiate";
  if (/^factor/i.test(s)) return "factor";
  if (/^expand/i.test(s)) return "expand";
  if (/^simplify/i.test(s)) return "simplify";
  if (/[a-zA-Z]/.test(s)) return "simplify";
  return "arithmetic";
}

// Strip leading command words like "differentiate ", "expand ", etc.
function stripCommand(s: string): string {
  return s.replace(/^(differentiate|expand|factor|simplify|d\/dx)\s*/i, "").trim();
}

// ---------------------------------------------------------------------------
// LaTeX rendering using mathjs nodes
// ---------------------------------------------------------------------------
export function toLatex(expr: string): string {
  try {
    const node = parse(expr);
    return node.toTex({ parenthesis: "auto", implicit: "hide" });
  } catch {
    return expr;
  }
}

// ---------------------------------------------------------------------------
// Linear equation: ax + b = cx + d  (collects coefficients of `x`)
// ---------------------------------------------------------------------------
function coefficientOf(expr: string, variable: string): { a: number; b: number } {
  // Build f(x) = a*x + b. We use two evaluations.
  try {
    const node = parse(expr);
    const f = (xv: number) => Number(node.evaluate({ [variable]: xv }));
    const f0 = f(0);
    const f1 = f(1);
    const a = f1 - f0;
    const b = f0;
    if (!Number.isFinite(a) || !Number.isFinite(b)) throw new Error("nonlinear");
    // Verify linearity at x=2
    const f2 = f(2);
    if (Math.abs(f2 - (2 * a + b)) > 1e-6) throw new Error("nonlinear");
    return { a, b };
  } catch (e) {
    throw e;
  }
}

const fmtNum = (n: number) => {
  if (Number.isInteger(n)) return n.toString();
  return Number(n.toFixed(6)).toString();
};

function solveLinear(input: string, variable = "x"): SolverResult {
  const [lhs, rhs] = input.split("=").map((s) => s.trim());
  if (!lhs || !rhs) return failed(input, "Equation must contain '='", "সমীকরণে '=' থাকতে হবে");
  let leftCoef, rightCoef;
  try {
    leftCoef = coefficientOf(lhs, variable);
    rightCoef = coefficientOf(rhs, variable);
  } catch {
    return failed(input, "This is not a linear equation in " + variable, "এটি " + variable + " এর রৈখিক সমীকরণ নয়");
  }
  const a = leftCoef.a - rightCoef.a; // coefficient of x after moving
  const b = rightCoef.b - leftCoef.b; // constant on right after moving

  const steps: Step[] = [];
  steps.push({
    en: `Original equation: ${lhs} = ${rhs}`,
    bn: `মূল সমীকরণ: ${lhs} = ${rhs}`,
    expr: `${lhs} = ${rhs}`,
  });
  steps.push({
    en: `Move all ${variable}-terms to the left and constants to the right.`,
    bn: `সকল ${variable}-পদকে বাঁ পাশে ও ধ্রুবকগুলো ডানে নিন।`,
    expr: `${fmtNum(a)}${variable} = ${fmtNum(b)}`,
  });

  if (a === 0) {
    if (b === 0) {
      return {
        ok: true, type: "equation", problem: input, problemTex: toLatex(input),
        answer: `${variable} ∈ ℝ (all real numbers)`,
        answerTex: `${variable}\\in\\mathbb{R}`,
        steps: [...steps, {
          en: "Both sides reduce to 0 = 0 — the equation is true for every value.",
          bn: "দুই পাশই 0 = 0 হয়ে যায় — প্রতিটি মান সমীকরণটি সত্য করে।",
        }],
      };
    }
    return {
      ok: true, type: "equation", problem: input, problemTex: toLatex(input),
      answer: "No solution",
      answerTex: "\\varnothing",
      steps: [...steps, {
        en: `${fmtNum(b)} ≠ 0 with no ${variable} — there is no value that satisfies the equation.`,
        bn: `${variable} বাদে ${fmtNum(b)} ≠ 0 — এমন কোনো মান নেই যা সমীকরণটি সিদ্ধ করে।`,
      }],
    };
  }
  const x = b / a;
  steps.push({
    en: `Divide both sides by ${fmtNum(a)}.`,
    bn: `উভয় পাশকে ${fmtNum(a)} দ্বারা ভাগ করুন।`,
    expr: `${variable} = ${fmtNum(b)} / ${fmtNum(a)}`,
  });
  steps.push({
    en: `${variable} = ${fmtNum(x)}`,
    bn: `${variable} = ${fmtNum(x)}`,
    expr: `${variable} = ${fmtNum(x)}`,
  });

  return {
    ok: true, type: "equation", problem: input, problemTex: toLatex(input),
    answer: `${variable} = ${fmtNum(x)}`,
    answerTex: `${variable}=${fmtNum(x)}`,
    steps,
  };
}

// ---------------------------------------------------------------------------
// Quadratic: ax^2 + bx + c = 0
// ---------------------------------------------------------------------------
function quadCoeffs(expr: string, variable: string): { a: number; b: number; c: number } {
  const node = parse(expr);
  const f = (xv: number) => Number(node.evaluate({ [variable]: xv }));
  const f0 = f(0);
  const f1 = f(1);
  const fm1 = f(-1);
  // f(0) = c; f(1) = a + b + c; f(-1) = a - b + c
  const c = f0;
  const a = (f1 + fm1) / 2 - c;
  const b = (f1 - fm1) / 2;
  if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(c)) throw new Error("invalid");
  // Validate at x=2
  const expected = a * 4 + b * 2 + c;
  if (Math.abs(f(2) - expected) > 1e-6) throw new Error("not quadratic");
  return { a, b, c };
}

function solveQuadratic(input: string, variable = "x"): SolverResult {
  const [lhsRaw, rhsRaw = "0"] = input.split("=").map((s) => s.trim());
  // Move everything to the left: LHS - RHS = 0
  const expr = `(${lhsRaw}) - (${rhsRaw})`;
  let coeffs;
  try {
    coeffs = quadCoeffs(expr, variable);
  } catch {
    return solveLinear(input, variable);
  }
  const { a, b, c } = coeffs;
  if (a === 0) return solveLinear(input, variable);

  const steps: Step[] = [];
  steps.push({
    en: `Original equation: ${lhsRaw} = ${rhsRaw}`,
    bn: `মূল সমীকরণ: ${lhsRaw} = ${rhsRaw}`,
    expr: `${lhsRaw} = ${rhsRaw}`,
  });
  steps.push({
    en: `Standard form: ${fmtNum(a)}${variable}^2 + ${fmtNum(b)}${variable} + ${fmtNum(c)} = 0`,
    bn: `সাধারণ রূপ: ${fmtNum(a)}${variable}² + ${fmtNum(b)}${variable} + ${fmtNum(c)} = 0`,
    expr: `${fmtNum(a)}${variable}^2 + ${fmtNum(b)}${variable} + ${fmtNum(c)} = 0`,
  });
  const D = b * b - 4 * a * c;
  steps.push({
    en: `Discriminant: D = b² − 4ac = (${fmtNum(b)})² − 4·${fmtNum(a)}·${fmtNum(c)} = ${fmtNum(D)}`,
    bn: `নির্ণায়ক: D = b² − 4ac = (${fmtNum(b)})² − 4·${fmtNum(a)}·${fmtNum(c)} = ${fmtNum(D)}`,
  });

  if (D < 0) {
    const re = -b / (2 * a);
    const im = Math.sqrt(-D) / (2 * a);
    steps.push({
      en: `D < 0 → two complex conjugate roots.`,
      bn: `D < 0 → দুটি জটিল অনুবন্ধী মূল।`,
    });
    const ans = `${variable} = ${fmtNum(re)} ± ${fmtNum(Math.abs(im))}i`;
    steps.push({ en: ans, bn: ans });
    return { ok: true, type: "quadratic", problem: input, problemTex: toLatex(input), answer: ans, answerTex: ans, steps };
  }

  const sqrtD = Math.sqrt(D);
  const x1 = (-b + sqrtD) / (2 * a);
  const x2 = (-b - sqrtD) / (2 * a);
  steps.push({
    en: `Apply quadratic formula: ${variable} = (−b ± √D) / 2a = (${fmtNum(-b)} ± ${fmtNum(sqrtD)}) / ${fmtNum(2 * a)}`,
    bn: `দ্বিঘাত সূত্র: ${variable} = (−b ± √D) / 2a = (${fmtNum(-b)} ± ${fmtNum(sqrtD)}) / ${fmtNum(2 * a)}`,
  });
  if (D === 0) {
    steps.push({ en: `Double root: ${variable} = ${fmtNum(x1)}`, bn: `দ্বিগুণ মূল: ${variable} = ${fmtNum(x1)}` });
    return { ok: true, type: "quadratic", problem: input, problemTex: toLatex(input), answer: `${variable} = ${fmtNum(x1)}`, answerTex: `${variable}=${fmtNum(x1)}`, steps };
  }
  steps.push({
    en: `${variable} = ${fmtNum(x1)}  or  ${variable} = ${fmtNum(x2)}`,
    bn: `${variable} = ${fmtNum(x1)}  অথবা  ${variable} = ${fmtNum(x2)}`,
  });
  return {
    ok: true,
    type: "quadratic",
    problem: input,
    problemTex: toLatex(input),
    answer: `${variable} = ${fmtNum(x1)}  or  ${fmtNum(x2)}`,
    answerTex: `${variable}=${fmtNum(x1)} \\;\\text{or}\\; ${fmtNum(x2)}`,
    steps,
  };
}

// ---------------------------------------------------------------------------
// System of two linear equations
// ---------------------------------------------------------------------------
function solveSystem(input: string): SolverResult {
  const eqs = input.split(/[;\n]/).map((s) => s.trim()).filter(Boolean);
  if (eqs.length !== 2) return failed(input, "Provide exactly two equations separated by ';'", "';' দিয়ে আলাদা করে ঠিক দুটি সমীকরণ দিন");
  // Detect variables
  const vars = Array.from(new Set((input.match(/[a-zA-Z]/g) ?? []).filter((c) => !"e".includes(c)))).slice(0, 2);
  if (vars.length < 2) return failed(input, "Need two variables", "দুটি ভেরিয়েবল প্রয়োজন");
  const [v1, v2] = vars;

  const linearForm = (eq: string): { a: number; b: number; c: number } | null => {
    const [lhs, rhs] = eq.split("=").map((s) => s.trim());
    if (!lhs || !rhs) return null;
    try {
      const node = parse(`(${lhs}) - (${rhs})`);
      const f = (xv: number, yv: number) => Number(node.evaluate({ [v1]: xv, [v2]: yv }));
      const c = -f(0, 0);
      const a = f(1, 0) + c;
      const b = f(0, 1) + c;
      // Validate
      if (Math.abs(f(2, 3) - (2 * a + 3 * b - c)) > 1e-6) return null;
      return { a, b, c };
    } catch {
      return null;
    }
  };

  const e1 = linearForm(eqs[0]);
  const e2 = linearForm(eqs[1]);
  if (!e1 || !e2) return failed(input, "Equations must be linear in two variables", "সমীকরণ দুটি দুই-ভেরিয়েবলের রৈখিক হতে হবে");

  const steps: Step[] = [];
  steps.push({
    en: `Equation 1: ${fmtNum(e1.a)}${v1} + ${fmtNum(e1.b)}${v2} = ${fmtNum(e1.c)}`,
    bn: `সমীকরণ ১: ${fmtNum(e1.a)}${v1} + ${fmtNum(e1.b)}${v2} = ${fmtNum(e1.c)}`,
  });
  steps.push({
    en: `Equation 2: ${fmtNum(e2.a)}${v1} + ${fmtNum(e2.b)}${v2} = ${fmtNum(e2.c)}`,
    bn: `সমীকরণ ২: ${fmtNum(e2.a)}${v1} + ${fmtNum(e2.b)}${v2} = ${fmtNum(e2.c)}`,
  });

  // Cramer's rule (a1 b1 / a2 b2)
  const det = e1.a * e2.b - e2.a * e1.b;
  if (det === 0) return failed(input, "System has no unique solution (parallel or identical lines)", "অনন্য সমাধান নেই (সমান্তরাল বা একই রেখা)");

  // Use elimination: multiply eq1 by e2.a, eq2 by e1.a, subtract → eliminate v1
  const m1 = e2.a, m2 = e1.a;
  steps.push({
    en: `Eliminate ${v1}: multiply Eq.1 by ${fmtNum(m1)} and Eq.2 by ${fmtNum(m2)}, then subtract.`,
    bn: `${v1} বাদ দিতে: সমীকরণ ১ কে ${fmtNum(m1)} ও সমীকরণ ২ কে ${fmtNum(m2)} দিয়ে গুণ করে বিয়োগ করুন।`,
  });
  const yCoef = m1 * e1.b - m2 * e2.b;
  const yConst = m1 * e1.c - m2 * e2.c;
  const y = yConst / yCoef;
  steps.push({
    en: `${fmtNum(yCoef)}${v2} = ${fmtNum(yConst)} → ${v2} = ${fmtNum(y)}`,
    bn: `${fmtNum(yCoef)}${v2} = ${fmtNum(yConst)} → ${v2} = ${fmtNum(y)}`,
  });
  const x = (e1.c - e1.b * y) / e1.a;
  steps.push({
    en: `Substitute back into Eq.1: ${v1} = (${fmtNum(e1.c)} − ${fmtNum(e1.b)}·${fmtNum(y)}) / ${fmtNum(e1.a)} = ${fmtNum(x)}`,
    bn: `সমীকরণ ১ এ বসান: ${v1} = (${fmtNum(e1.c)} − ${fmtNum(e1.b)}·${fmtNum(y)}) / ${fmtNum(e1.a)} = ${fmtNum(x)}`,
  });
  const ans = `${v1} = ${fmtNum(x)}, ${v2} = ${fmtNum(y)}`;
  return { ok: true, type: "system", problem: input, problemTex: toLatex(eqs[0]) + ",\\ " + toLatex(eqs[1]), answer: ans, answerTex: ans, steps };
}

// ---------------------------------------------------------------------------
// Simplify / Expand / Factor (mathjs based)
// ---------------------------------------------------------------------------
function simplifyExpr(input: string, type: "simplify" | "expand" | "factor"): SolverResult {
  const stripped = stripCommand(input);
  try {
    let result: MathNode;
    const steps: Step[] = [];
    steps.push({ en: `Input: ${stripped}`, bn: `ইনপুট: ${stripped}`, expr: stripped });

    if (type === "expand") {
      // Rationalize multiplies out polynomial products and collects like terms.
      result = rationalize(stripped) as MathNode;
      steps.push({ en: "Apply distributive law and combine like terms.", bn: "বিতরণ সূত্র প্রয়োগ করে সদৃশ পদগুলো একত্র করুন।" });
    } else if (type === "factor") {
      result = simplify(stripped);
      steps.push({ en: "Simplify; mathjs returns a factored or simplified form when possible.", bn: "সরলীকরণ করুন; সম্ভব হলে গুণনীয়ক রূপ দেখানো হয়।" });
    } else {
      result = simplify(stripped);
      steps.push({ en: "Combine like terms and reduce the expression.", bn: "সদৃশ পদ একত্র করে সরলীকরণ করুন।" });
    }
    const out = result.toString();
    steps.push({ en: `= ${out}`, bn: `= ${out}`, expr: out });
    return {
      ok: true, type, problem: stripped, problemTex: toLatex(stripped),
      answer: out, answerTex: result.toTex({ parenthesis: "auto", implicit: "hide" }),
      steps,
    };
  } catch (e) {
    return failed(input, "Could not simplify this expression.", "এই রাশিটি সরলীকরণ করা গেল না।");
  }
}

// ---------------------------------------------------------------------------
// Differentiate
// ---------------------------------------------------------------------------
function differentiate(input: string, variable = "x"): SolverResult {
  // Input is already command-stripped & normalised. Drop wrapping parens if any.
  let stripped = input;
  if (stripped.startsWith("(") && stripped.endsWith(")")) {
    // only strip if these parens are matched as outermost
    let depth = 0, outermost = true;
    for (let i = 0; i < stripped.length; i++) {
      if (stripped[i] === "(") depth++;
      else if (stripped[i] === ")") { depth--; if (depth === 0 && i < stripped.length - 1) { outermost = false; break; } }
    }
    if (outermost) stripped = stripped.slice(1, -1);
  }
  try {
    const node = parse(stripped);
    const d = derivative(node, variable);
    const steps: Step[] = [];
    steps.push({ en: `Differentiate with respect to ${variable}: ${stripped}`, bn: `${variable} এর সাপেক্ষে অন্তরীকরণ: ${stripped}`, expr: stripped });
    steps.push({
      en: `Apply rules term-by-term: power rule d/d${variable}(${variable}^n) = n·${variable}^(n−1), constant rule d/d${variable}(c) = 0, sum rule.`,
      bn: `প্রতিটি পদে নিয়ম প্রয়োগ করুন: পাওয়ার রুল d/d${variable}(${variable}^n) = n·${variable}^(n−1), ধ্রুবক নিয়ম, যোগ নিয়ম।`,
    });
    const simplified = simplify(d).toString();
    steps.push({ en: `= ${simplified}`, bn: `= ${simplified}`, expr: simplified });
    return {
      ok: true, type: "differentiate", problem: stripped, problemTex: toLatex(stripped),
      answer: simplified, answerTex: simplify(d).toTex({ parenthesis: "auto", implicit: "hide" }),
      steps,
    };
  } catch {
    return failed(input, "Could not differentiate this expression.", "এই রাশিটি অন্তরীকরণ করা গেল না।");
  }
}

// ---------------------------------------------------------------------------
// Arithmetic / BODMAS — show order of evaluation
// ---------------------------------------------------------------------------
function solveArithmetic(input: string): SolverResult {
  try {
    const value = evaluate(input);
    const steps: Step[] = [];
    steps.push({ en: `Expression: ${input}`, bn: `রাশি: ${input}`, expr: input });

    let working = input;
    // Repeatedly resolve innermost parentheses
    let safety = 20;
    while (/\(/.test(working) && safety-- > 0) {
      const m = working.match(/\(([^()]+)\)/);
      if (!m) break;
      const inner = m[1];
      const innerVal = evaluate(inner);
      const replaced = working.replace(m[0], fmtNum(Number(innerVal)));
      steps.push({
        en: `Brackets first: (${inner}) = ${fmtNum(Number(innerVal))}`,
        bn: `প্রথমে বন্ধনী: (${inner}) = ${fmtNum(Number(innerVal))}`,
        expr: replaced,
      });
      working = replaced;
    }
    // Multiplication / division pass
    safety = 20;
    while (/[\d.]+\s*[*/]\s*[\d.]+/.test(working) && safety-- > 0) {
      const m = working.match(/(-?[\d.]+)\s*([*/])\s*(-?[\d.]+)/);
      if (!m) break;
      const v = evaluate(m[0]);
      const replaced = working.replace(m[0], fmtNum(Number(v)));
      steps.push({
        en: `Multiplication / division: ${m[0]} = ${fmtNum(Number(v))}`,
        bn: `গুণ / ভাগ: ${m[0]} = ${fmtNum(Number(v))}`,
        expr: replaced,
      });
      working = replaced;
    }
    // Addition / subtraction pass
    safety = 20;
    while (/[\d.]+\s*[+\-]\s*[\d.]+/.test(working) && safety-- > 0 && working !== fmtNum(Number(value))) {
      const m = working.match(/(-?[\d.]+)\s*([+\-])\s*([\d.]+)/);
      if (!m) break;
      const v = evaluate(m[0]);
      const replaced = working.replace(m[0], fmtNum(Number(v)));
      steps.push({
        en: `Addition / subtraction: ${m[0]} = ${fmtNum(Number(v))}`,
        bn: `যোগ / বিয়োগ: ${m[0]} = ${fmtNum(Number(v))}`,
        expr: replaced,
      });
      working = replaced;
    }
    const ans = fmtNum(Number(value));
    if (steps[steps.length - 1].expr !== ans) {
      steps.push({ en: `= ${ans}`, bn: `= ${ans}`, expr: ans });
    }
    return { ok: true, type: "arithmetic", problem: input, problemTex: toLatex(input), answer: ans, answerTex: ans, steps };
  } catch {
    return failed(input, "Could not evaluate this expression.", "এই রাশির মান বের করা গেল না।");
  }
}

// ---------------------------------------------------------------------------
// Failure helper
// ---------------------------------------------------------------------------
function failed(input: string, en: string, bn: string): SolverResult {
  return {
    ok: false,
    type: "auto",
    problem: input,
    problemTex: toLatex(input),
    steps: [],
    error: { en, bn },
  };
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------
export interface SolveOptions {
  type?: ProblemType;
  variable?: string;
}

export function solveProblem(rawInput: string, opts: SolveOptions = {}): SolverResult {
  if (!rawInput.trim()) return failed(rawInput, "Enter a problem.", "একটি সমস্যা লিখুন।");

  // Detect type from RAW input (so command words like 'expand', 'd/dx' survive)
  let type = opts.type ?? "auto";
  if (type === "auto") type = detectType(rawInput);

  // Strip command words, then normalise
  const stripped = stripCommand(rawInput.replace(/^d\/?dx\s*/i, ""));
  const input = normalize(stripped);
  if (!input) return failed(rawInput, "Enter a problem.", "একটি সমস্যা লিখুন।");

  const variable = opts.variable ?? "x";

  try {
    switch (type) {
      case "system": return solveSystem(input);
      case "quadratic": return solveQuadratic(input, variable);
      case "equation": return solveLinear(input, variable);
      case "simplify":
      case "expand":
      case "factor": return simplifyExpr(input, type);
      case "differentiate": return differentiate(input, variable);
      case "arithmetic": return solveArithmetic(input);
      default: return solveArithmetic(input);
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : "unknown";
    return failed(rawInput, "Could not solve: " + msg, "সমাধান করা গেল না: " + msg);
  }
}
